import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ClickAwayListener,
  Divider,
  InputAdornment,
  List,
  Paper,
  Popper,
  TextField,
  Typography,
  Grid,
  styled,
  ListItemButton,
  Grow,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import Fuse from "fuse.js";
import useBreakpoints from "../../../hooks/useBreakpoints";
import useDebounce from "../../../hooks/useDebounce";

const Result = styled(Typography)(({ theme }) => ({
  wordWrap: "break-word",
  overflowWrap: "break-word",
}));

const SearchResults = ({
  anchorEl,
  open,
  onClose,
  onSelect,
  searchResults,
}) => {
  return (
    <Popper
      open={open}
      anchorEl={anchorEl}
      role={undefined}
      transition
      disablePortal={false}
      placement="bottom-start"
      style={{ zIndex: 3 }}
    >
      {({ TransitionProps, placement }) => (
        <Grow
          {...TransitionProps}
          style={{
            transformOrigin:
              placement === "bottom" ? "center top" : "center bottom",
          }}
        >
          <Paper
            style={{
              width: "400px",
              height: "300px",
              overflowY: "auto",
            }}
          >
            <ClickAwayListener onClickAway={onClose}>
              <List dense component="nav" aria-label="main mailbox folders">
                {searchResults?.slice(0, 49)?.map(({ item }, i) => (
                  <React.Fragment key={i}>
                    <ListItemButton
                      sx={{
                        flexDirection: "column",
                        alignItems: "flex-start",
                        width: "100%",
                      }}
                      onClick={(e) => {
                        onSelect(item);
                        onClose(e);
                      }}
                    >
                      <Typography variant="subtitle1">
                        {item?.well_ndx}
                      </Typography>
                      <Grid container>
                        <Grid item xs={6}>
                          <Typography variant="caption">
                            Associated Permit
                          </Typography>
                          <Result variant="body1">
                            {item?.assoc_permit_ndx || "N/A"}
                          </Result>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption">
                            Company Name
                          </Typography>
                          <Result variant="body1">
                            {item?.name_company || "N/A"}
                          </Result>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption">
                            Contact First Name
                          </Typography>
                          <Result variant="body1">
                            {item?.name_first || "N/A"}
                          </Result>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="caption">
                            Contact Last Name
                          </Typography>
                          <Result variant="body1">
                            {item?.name_last || "N/A"}
                          </Result>
                        </Grid>
                      </Grid>
                    </ListItemButton>
                    <Divider />
                  </React.Fragment>
                ))}
              </List>
            </ClickAwayListener>
          </Paper>
        </Grow>
      )}
    </Popper>
  );
};

const Search = ({ onSelect, data }) => {
  const searchRef = useRef(null);
  const { isXs } = useBreakpoints();

  const [value, setValue] = useState("");
  const debouncedSearchValue = useDebounce(value, 200);
  const [searchResults, setSearchResults] = useState([]);
  const [open, setOpen] = useState(false);

  const size = isXs ? "small" : "medium";

  useEffect(() => {
    setOpen(!!searchResults?.length);
  }, [searchResults]);

  const fuzzySearcher = useMemo(() => {
    if (data) {
      return new Fuse(data, {
        ignoreLocation: true,
        keys: ["well_ndx", "name_company", "name_company", "assoc_permit_ndx"],
      });
    }
  }, [data]);

  const handleClose = (event) => {
    if (searchRef.current && searchRef.current.contains(event.target)) {
      return;
    }

    setOpen(false);
  };

  const handleChange = (event) => {
    setValue(event?.target?.value);
  };

  useEffect(() => {
    const results = fuzzySearcher && fuzzySearcher.search(debouncedSearchValue);
    setSearchResults(results);
  }, [debouncedSearchValue, fuzzySearcher]);

  return (
    <>
      <TextField
        id="outlined-basic"
        label="Wells Search"
        autoComplete="off"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        onChange={handleChange}
        onFocus={() => !!value && setOpen(true)}
        placeholder="Well Attributes"
        ref={searchRef}
        style={{
          width: "100%",
          alignSelf: "center",
          marginTop: "8px",
        }}
        type="search"
        value={value}
        variant="outlined"
        size={size}
      />
      <SearchResults
        anchorEl={searchRef?.current}
        onClose={handleClose}
        onSelect={onSelect}
        open={open}
        searchResults={searchResults}
      />
    </>
  );
};

export default Search;
